/**
 * Pure helpers that build the prop bag for the single-order header
 * on the external single-order layout.
 *
 * @module singleOrderForExternal/utils/headerPropsBuilder
 */
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { formatDateToUTCWithFormat } from "../../../utils/formatDate";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../utils/stringModifications";

/** Date format used for the header date-of-service display. */
export const HEADER_DOS_FORMAT = "MM/DD/YYYY";

/**
 * @param coverages - Coverage tiers returned for the order
 * @returns The `payer_name` of the primary coverage, or `""` when none is found
 */
export const getPrimaryCoveragePayerName = (
  coverages: SingleCoverage[] | undefined,
): string =>
  coverages?.find((coverage) => coverage.coverage_type === "primary")
    ?.payer_name ?? "";

/** Inputs required to build the external header prop bag. */
interface BuildHeaderPropsInput {
  generalOrderDetails:
    { order?: GeneralOrderDetails | null } | null | undefined;
  coverageDetails: { coverages?: SingleCoverage[] } | null | undefined;
  authOnFileDetails: { auth_on_file?: AuthOnFile } | null | undefined;
}

/**
 * Builds the prop bag consumed by the shared single-order `Header` from
 * order, coverage, and auth-on-file data.
 *
 * @param input - Order / coverage / auth-on-file data
 * @returns Prop bag with formatted patient, provider, DOS, and insurance fields
 */
export const buildHeaderProps = ({
  generalOrderDetails,
  coverageDetails,
  authOnFileDetails,
}: BuildHeaderPropsInput) => {
  const order = generalOrderDetails?.order;
  return {
    orderId: order?.order_id ?? "",
    status: authOnFileDetails?.auth_on_file?.auth_status ?? "",
    patientMrn: order?.demographics?.patient_id ?? "",
    patientName: capitalizeString(
      generateFullName(
        order?.demographics?.first_name ?? "",
        "",
        order?.demographics?.last_name ?? "",
      ),
    ),
    dateOfService: formatDateToUTCWithFormat(
      order?.payload?.date_of_service ?? "",
      HEADER_DOS_FORMAT,
    ),
    location: order?.payload?.location ?? "",
    providerName: rotateLastSegmentFirst(
      order?.payload?.practitioner_name ?? "",
    ),
    insuranceName: getPrimaryCoveragePayerName(coverageDetails?.coverages),
  };
};
